import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import ListItemRadioButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemRadioButton';
import Flex from '@wix/da-ds/pkg/Flex/Flex';
import CoreExclusiveFeature from '../../indicators/CoreExclusiveFeature/CoreExclusiveFeature';
import PriceInputWithEarnings from '../../DollarPriceInput/variants/PriceInputWithEarnings';
import { PremiumFolderType } from '../../types/gallection';

import s from './PremiumForm.scss';

const PLACEHOLDER_FOLDER_PRICE = 10;

export interface Props {
  fullWidth?: boolean;
}

export const PremiumForm: React.FC<Props> = ({ fullWidth }) => {
  const { t } = useTranslation();
  const { control, errors, watch } = useFormContext();
  const folderType = watch('folderType');

  return (
    <div className={cn(s['root'], fullWidth && s['full-width'])}>
      <Controller
        control={control}
        name="folderType"
        render={({ onChange, value }) => {
          const handleOnChange = (isSelected, newValue) => {
            onChange(newValue);
          };
          return (
            <>
              <ListItemRadioButton
                value={PremiumFolderType.Regular}
                checked={value === PremiumFolderType.Regular}
                onChange={handleOnChange}
                label={
                  <Flex gap={8}>
                    <div>
                      {t('mediapopup.newFolderPopup.gallery.radioButtons.regularFolder.label')}
                    </div>
                  </Flex>
                }
                isLabelBold={true}
                subLabel={t('mediapopup.newFolderPopup.gallery.radioButtons.regularFolder.hint')}
                name="folderType"
              />
              <ListItemRadioButton
                value={PremiumFolderType.Watchers}
                checked={value === PremiumFolderType.Watchers}
                onChange={handleOnChange}
                label={
                  <Flex gap={8}>
                    <div>
                      {t('mediapopup.newFolderPopup.gallery.radioButtons.watchersOnly.label')}
                    </div>
                    <CoreExclusiveFeature withoutBuyCTA />
                  </Flex>
                }
                isLabelBold={true}
                subLabel={t('mediapopup.newFolderPopup.gallery.radioButtons.watchersOnly.hint')}
                name="folderType"
              />
              <ListItemRadioButton
                value={PremiumFolderType.Paid}
                checked={value === PremiumFolderType.Paid}
                onChange={handleOnChange}
                label={
                  <Flex gap={8}>
                    <div>{t('mediapopup.newFolderPopup.gallery.radioButtons.paid.label')}</div>
                    <CoreExclusiveFeature withoutBuyCTA />
                  </Flex>
                }
                isLabelBold={true}
                subLabel={t('mediapopup.newFolderPopup.gallery.radioButtons.paid.hint')}
                name="folderType"
              />
            </>
          );
        }}
      />

      {folderType === 'paid' && (
        <Controller
          control={control}
          name="price"
          render={({ onChange, value, name }) => {
            return (
              <div className={s['premium-selected']}>
                <PriceInputWithEarnings
                  label={t('mediapopup.newFolderPopup.a11y.priceInputLabel')}
                  className={s['price-input']}
                  error={errors[name]?.message}
                  isRequired
                  placeholder={PLACEHOLDER_FOLDER_PRICE}
                  productType="gallery"
                  onChange={onChange}
                  value={value}
                  name={name}
                />
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

PremiumForm.displayName = 'PremiumForm';

export default PremiumForm;
